'use client';

import { deleteMemoryAction } from '@/app/actions/memory/delete';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { handleError } from '@/lib/error/handle';
import { useProject } from '@/providers/project';
import { type MemoryItem, useMemoryStore } from '@/stores/memory';
import { formatDistanceToNow } from 'date-fns';
import { CalendarIcon, DatabaseIcon, TagIcon, TrashIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

type MemorySearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onMemorySelect?: (memory: MemoryItem) => void;
};

export const MemorySearchModal = ({
  isOpen,
  onClose,
  onMemorySelect,
}: MemorySearchModalProps) => {
  const [deleting, setDeleting] = useState<string | null>(null);
  const project = useProject();

  const {
    filteredMemories,
    loading,
    searchTerm,
    setSearchTerm,
    fetchMemories,
    removeMemory,
  } = useMemoryStore();

  // Fetch memories when modal opens
  useEffect(() => {
    if (isOpen && project?.id) {
      fetchMemories(project.id);
    }
  }, [isOpen, project?.id, fetchMemories]);

  const handleDeleteMemory = useCallback(
    async (memoryId: string, event: React.MouseEvent) => {
      event.stopPropagation();

      if (deleting) return;

      setDeleting(memoryId);
      try {
        const response = await deleteMemoryAction({ memoryId });

        if ('error' in response) {
          throw new Error(response.error);
        }

        toast.success('Memory deleted successfully');
        removeMemory(memoryId);
      } catch (error) {
        handleError('Error deleting memory', error);
      } finally {
        setDeleting(null);
      }
    },
    [deleting, removeMemory]
  );

  const handleMemorySelect = useCallback(
    (memory: MemoryItem) => {
      onMemorySelect?.(memory);
      onClose();
    },
    [onMemorySelect, onClose]
  );

  const truncateContent = (content: string, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return `${content.substring(0, maxLength)}...`;
  };

  const getDisplayContent = (memory: MemoryItem) => {
    // Try to get content from various fields in order of preference
    return (
      memory.content || memory.summary || memory.title || 'No content available'
    );
  };

  const getDisplayTitle = (memory: MemoryItem) => {
    return memory.title || truncateContent(getDisplayContent(memory), 50);
  };

  useEffect(() => {
    if (isOpen) {
      fetchMemories();
    }
  }, [fetchMemories, isOpen]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) {
        fetchMemories();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [fetchMemories, isOpen]);

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={onClose}
      title="Manage Memories"
      description="Search and manage your saved memories"
    >
      <CommandInput
        placeholder="Search memories..."
        value={searchTerm}
        onValueChange={setSearchTerm}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>
          {loading ? 'Loading memories...' : 'No memories found.'}
        </CommandEmpty>
        <CommandGroup>
          {filteredMemories.map((memory) => (
            <CommandItem
              key={`${memory.id}-${memory.createdAt}`}
              onSelect={() => handleMemorySelect(memory)}
              className="flex cursor-pointer flex-col items-start gap-2 p-4"
            >
              <div className="flex w-full items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">
                    {getDisplayTitle(memory)}
                  </p>
                  {memory.summary && memory.summary !== memory.title && (
                    <p className="mt-1 line-clamp-2 text-muted-foreground text-xs">
                      {truncateContent(memory.summary, 120)}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                    <CalendarIcon size={12} />
                    <span>
                      {memory.createdAt
                        ? formatDistanceToNow(new Date(memory.createdAt), {
                            addSuffix: true,
                          })
                        : 'Unknown date'}
                    </span>
                    {memory.type && (
                      <>
                        <span>•</span>
                        <DatabaseIcon size={12} />
                        <span>{String(memory.type)}</span>
                      </>
                    )}
                    {(() => {
                      if (
                        memory.metadata?.category &&
                        typeof memory.metadata.category === 'string'
                      ) {
                        try {
                          const tags = JSON.parse(memory.metadata.category);
                          if (Array.isArray(tags) && tags.length > 0) {
                            return (
                              <>
                                <span>•</span>
                                <TagIcon size={10} className="mr-1" />
                                <div className="flex flex-wrap gap-1">
                                  {tags.slice(0, 3).map((tag: string) => (
                                    <Badge
                                      key={tag}
                                      variant="outline"
                                      className="h-5 px-1.5 text-xs"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </>
                            );
                          }
                        } catch {
                          // Ignore parsing errors
                        }
                      }
                      return null;
                    })()}
                    {memory.status && memory.status !== 'done' && (
                      <>
                        <span>•</span>
                        <span className="capitalize">{memory.status}</span>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => handleDeleteMemory(memory.id, e)}
                  disabled={deleting === memory.id}
                  className="ml-2 h-6 w-6 shrink-0 p-0 hover:bg-destructive/10 hover:text-destructive"
                >
                  <TrashIcon size={12} />
                </Button>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
